"use client"

import { Badge } from "@/components/atoms/Badge"
import { Button } from "@/components/atoms"
import { HELPERS } from "@/utils"
import { Icon } from "@iconify/react"
import PATH from "@/routes/path"
import ReactPixel from "react-facebook-pixel"
import { useHistory } from "react-router-dom"
import { useSelector } from "react-redux"

const MentoringCard = ({ mentoring, isLast, onContact, onAddToCart, onBuy, loadingStates }) => {
  const history = useHistory()
  const client = useSelector((state) => state.client)
  const userProfile = useSelector((state) => state.auth.profile)
  const clientData = useSelector((state) => state.client.payload)

  const formatCurrency = (amount) => {
    const numAmount = Number(amount)
    if (isNaN(numAmount) || numAmount <= 0) {
      return "Rp 0"
    }
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount)
  }

  const handleContactMentor = () => {
    HELPERS.sendEventGA("contact_mentoring", {
      mentoring_id: mentoring.id,
      mentoring_topic: mentoring.topic,
      relevance_score: mentoring.relevanceScore,
      page_source: "Assessment Results",
    })

    ReactPixel.track("Contact", {
      method: "WhatsApp Mentoring",
      contact_id: client.payload?.contact_phone,
      mentoring_topic: mentoring.topic,
    })

    if (onContact) {
      onContact(mentoring)
    } else if (client.payload?.contact_phone) {
      const message = `Halo, saya tertarik dengan layanan mentoring "${mentoring.title}". Bisakah saya mendapatkan informasi lebih lanjut tentang:

Program: ${mentoring.title}
Topik: ${mentoring.topic}
Relevansi: ${mentoring.relevanceScore}%

Terima kasih!`
      const whatsappURL = `https://wa.me/${HELPERS.parsePhone(client.payload.contact_phone)}?text=${encodeURIComponent(message)}`
      window.open(whatsappURL, "_blank")
    } else {
      history.push(PATH.CONTACT)
    }
  }

  const handleAddToCart = () => {
    // Prevent individual purchase when in bundle mode
    if (mentoring.isInBundle) {
      alert('Mentoring ini tersedia dengan harga diskon saat dibeli sebagai bundle. Silakan gunakan tombol "Beli Semua" untuk mendapatkan harga terbaik!')
      return;
    }

    if (
      onAddToCart &&
      mentoring.price > 0 &&
      !mentoring.is_purchased &&
      !mentoring.is_owned &&
      mentoring.can_purchase
    ) {
      // Use original individual price when not in bundle
      const individualPrice = mentoring.originalPrice || mentoring.price;
      const mentoringProduct = {
        productId: mentoring.id,
        title: mentoring.title,
        price: individualPrice, // Use individual price
        originalPrice: individualPrice,
        discount: 0, // No discount for individual purchase
        type: "mentoring",
        thumbnail: mentoring.image || null,
        instructor: mentoring.mentor,
        category: "Mentoring",
        slug: mentoring.topic,
        duration_minutes: mentoring.duration_minutes,
        can_purchase: mentoring.can_purchase,
        is_purchased: mentoring.is_purchased,
        is_owned: mentoring.is_owned,
        ownership_type: mentoring.ownership_type,
      }
      onAddToCart(mentoringProduct)
    }
  }

  const handleBuyNow = () => {
    // Prevent individual purchase when in bundle mode
    if (mentoring.isInBundle) {
      alert('Mentoring ini tersedia dengan harga diskon saat dibeli sebagai bundle. Silakan gunakan tombol "Beli Semua" untuk mendapatkan harga terbaik!')
      return;
    }

    if (onBuy && mentoring.price > 0 && !mentoring.is_purchased && !mentoring.is_owned && mentoring.can_purchase) {
      // Use original individual price when not in bundle
      const individualPrice = mentoring.originalPrice || mentoring.price;
      const mentoringProduct = {
        productId: mentoring.id,
        title: mentoring.title,
        price: individualPrice, // Use individual price
        originalPrice: individualPrice,
        discount: 0, // No discount for individual purchase
        type: "mentoring",
        thumbnail: mentoring.image || null,
        instructor: mentoring.mentor,
        category: "Mentoring",
        slug: mentoring.topic,
        duration_minutes: mentoring.duration_minutes,
        can_purchase: mentoring.can_purchase,
        is_purchased: mentoring.is_purchased,
        is_owned: mentoring.is_owned,
        ownership_type: mentoring.ownership_type,
      }
      onBuy(mentoringProduct)
    }
  }

  const handleScheduleMentoring = () => {
    const userEmail = userProfile?.email || "email belum tersedia"
    const mentoringTitle = mentoring.title || "mentoring"
    const message = `Halo saya sudah membeli sesi 1-on-1 mentoring ${mentoringTitle} dengan akun ${userEmail}. Minta tolong dijadwalkan sesinya ya. Terima kasih`
    const adminPhone = clientData?.contact_phone || "628123456789"
    const whatsappURL = `https://wa.me/${HELPERS.parsePhone(adminPhone)}?text=${encodeURIComponent(message)}`
    window.open(whatsappURL, "_blank")
  }

  return (
    <div
      className={`relative flex items-start w-full ${
        !isLast
          ? "md:after:content-[''] md:after:w-0.5 md:after:h-full md:after:bg-gray-300 md:after:inline-block md:after:absolute md:after:-bottom-6 md:after:left-4"
          : ""
      }`}
      style={{
        "--after-bg": "#d0cfd2", // GRAY_APP.300
      }}
    >
      {/* Timeline Circle - Enhanced with brand colors */}
      <div
        className="w-8 h-8 hidden md:block border-2 border-solid rounded-2xl z-10 relative mr-4 lg:mr-8 flex-shrink-0 shadow-sm"
        style={{
          backgroundColor: "#FAF9FB", // WHITE_APP
          borderColor: "#05D172", // SECONDARY.DEFAULT
        }}
      >
        <div
          className="w-full h-full rounded-full flex items-center justify-center"
          style={{ backgroundColor: "#05D172" }} // SECONDARY.DEFAULT
        >
          <Icon icon="material-symbols:person" className="w-4 h-4 text-white" />
        </div>
      </div>

      {/* Main Card - Improved gradient and styling */}
      <div
        className="p-3 sm:p-4 lg:p-6 rounded-xl shadow-lg mb-4 sm:mb-6 w-full max-w-full md:max-w-2xl lg:max-w-4xl border border-opacity-20"
        style={{
          background: "linear-gradient(135deg, #F3F1FE 0%, #E6E3FC 50%, #D1CCF9 100%)", // DETAIL to PRIMARY.100 to PRIMARY.200
          borderColor: "#926fe5", // PRIMARY.DEFAULT
        }}
      >
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Header - Better color hierarchy */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex items-start gap-2 sm:gap-3 flex-1">
              <div
                className="p-1.5 sm:p-2 rounded-lg flex-shrink-0 shadow-sm"
                style={{ backgroundColor: "rgba(146, 111, 229, 0.1)" }} // PRIMARY with opacity
              >
                <Icon
                  icon="material-symbols:psychology"
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  style={{ color: "#926fe5" }} // PRIMARY.DEFAULT
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                  <Badge
                    variant="secondary"
                    className="px-3 py-1 text-xs font-semibold rounded-full w-fit border"
                    style={{
                      backgroundColor: "rgba(146, 111, 229, 0.15)", // PRIMARY with opacity
                      color: "#7344BE", // PRIMARY.700
                      borderColor: "rgba(146, 111, 229, 0.3)",
                    }}
                  >
                    MENTORING
                  </Badge>
                  <span
                    className="text-xs font-medium"
                    style={{ color: "#636363" }} // STEPPER_SUB
                  >
                    Relevansi: {mentoring.relevanceScore}%
                  </span>
                </div>
                <h3
                  className="font-bold text-lg sm:text-xl leading-6 sm:leading-7 break-words"
                  style={{
                    fontFamily: "Raleway, Helvetica",
                    color: "#191919", // STEPPER_TITTLE
                  }}
                >
                  {mentoring.title}
                </h3>
              </div>
            </div>
          </div>

          {/* Description - Better contrast */}
          <div
            className="p-3 rounded-lg border border-opacity-20"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              borderColor: "#926fe5", // PRIMARY.DEFAULT
            }}
          >
            <p
              className="text-sm leading-5 sm:leading-6"
              style={{ color: "#636363" }} // STEPPER_SUB
            >
              {mentoring.description}
            </p>
          </div>

          {/* Reasons - Enhanced styling */}
          <div
            className="p-3 rounded-lg border border-opacity-20"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              borderColor: "#926fe5", // PRIMARY.DEFAULT
            }}
          >
            <h4
              className="font-semibold text-sm mb-2"
              style={{ color: "#191919" }} // STEPPER_TITTLE
            >
              Mengapa cocok untuk Anda:
            </h4>
            <ul className="space-y-1.5">
              {(mentoring.reasons || []).map((reason, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <Icon
                    icon="material-symbols:check-circle"
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    style={{ color: "#05D172" }} // SECONDARY.DEFAULT
                  />
                  <span
                    className="break-words"
                    style={{ color: "#636363" }} // STEPPER_SUB
                  >
                    {reason}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Price and Purchase Section - Better color coordination */}
          {!mentoring.is_purchased &&
            !mentoring.is_owned &&
            mentoring.can_purchase &&
            mentoring.price &&
            mentoring.price > 0 && (
              <div
                className="p-3 sm:p-4 rounded-lg border-t-2"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  borderTopColor: "#05D172", // SECONDARY.DEFAULT
                }}
              >
                <div className="flex flex-col gap-3 sm:gap-4">
                  {/* Mentor Info and Duration */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Icon
                        icon="material-symbols:person-outline"
                        className="w-4 h-4 flex-shrink-0"
                        style={{ color: "#636363" }} // STEPPER_SUB
                      />
                      <span
                        className="text-sm font-medium"
                        style={{ color: "#636363" }} // STEPPER_SUB
                      >
                        {mentoring.mentor}
                      </span>
                    </div>
                    <p
                      className="text-xs"
                      style={{ color: "#636363" }} // STEPPER_SUB
                    >
                      Sesi mentoring personal 1-on-1 ({mentoring.duration_minutes || 60} menit)
                    </p>
                  </div>

                  {/* Price and Buttons */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* Price Display */}
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {mentoring.originalPrice && mentoring.originalPrice > mentoring.price && (
                          <span
                            className="text-sm line-through"
                            style={{ color: "#b0aeb2" }} // GRAY_APP.DEFAULT
                          >
                            {formatCurrency(mentoring.originalPrice)}
                          </span>
                        )}
                        <span
                          className="font-bold text-lg sm:text-xl"
                          style={{ color: "#05D172" }} // SECONDARY.DEFAULT
                        >
                          {formatCurrency(mentoring.price)}
                        </span>
                        {mentoring.discount && Number(mentoring.discount) > 0 && (
                          <Badge
                            variant="discount"
                            className="px-2 py-1 text-xs rounded-full border"
                            style={{
                              backgroundColor: "rgba(225, 108, 82, 0.1)", // TERTIARY with opacity
                              color: "#e16c52", // TERTIARY.DEFAULT
                              borderColor: "rgba(225, 108, 82, 0.3)",
                            }}
                          >
                            Disc {Math.round(Number(mentoring.discount))}%
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Purchase Buttons - Better color scheme */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      {mentoring.isInBundle && (
                        <div className="w-full text-center">
                          <div className="bg-gradient-to-r from-purple-100 to-green-100 p-3 rounded-lg border border-purple-200">
                            <p className="text-xs text-purple-700 font-medium mb-2">
                              💰 Harga bundle lebih hemat!
                            </p>
                            <p className="text-xs text-gray-600">
                              Gunakan tombol "Beli Semua" di bawah untuk mendapatkan diskon bundle
                            </p>
                          </div>
                        </div>
                      )}
                      {!mentoring.isInBundle && onAddToCart && (
                        <Button
                          variant={Button.VARIANTS.GRAY_OUTLINE}
                          size={Button.SIZES.MEDIUM}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-full font-semibold text-sm border-2 min-w-0 transition-all duration-200 hover:shadow-md"
                          style={{
                            borderColor: "#926fe5", // PRIMARY.DEFAULT
                            color: "#926fe5", // PRIMARY.DEFAULT
                            backgroundColor: "transparent",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "rgba(146, 111, 229, 0.1)"
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "transparent"
                          }}
                          onClick={handleAddToCart}
                          disabled={loadingStates?.addToCart?.[mentoring.id]}
                        >
                          {loadingStates?.addToCart?.[mentoring.id] ? (
                            <>
                              <Icon icon="eos-icons:loading" className="w-4 h-4 sm:mr-1 flex-shrink-0" />
                              <span className="hidden sm:inline truncate">Adding...</span>
                              <span className="sm:hidden">⏳</span>
                            </>
                          ) : (
                            <>
                              <Icon
                                icon="material-symbols:shopping-cart-outline"
                                className="w-4 h-4 sm:mr-1 flex-shrink-0"
                              />
                              <span className="hidden sm:inline truncate">Tambah</span>
                              <span className="sm:hidden">+</span>
                            </>
                          )}
                        </Button>
                      )}
                      {!mentoring.isInBundle && onBuy && (
                        <Button
                          variant={Button.VARIANTS.SECONDARY}
                          size={Button.SIZES.MEDIUM}
                          className="flex-1 sm:flex-none px-3 sm:px-6 py-2 rounded-full font-semibold text-sm text-white min-w-0 transition-all duration-200 hover:shadow-md"
                          style={{
                            backgroundColor: "#05D172", // SECONDARY.DEFAULT
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#00BC63" // SECONDARY.600
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "#05D172"
                          }}
                          onClick={handleBuyNow}
                          disabled={loadingStates?.buyNow?.[mentoring.id]}
                        >
                          {loadingStates?.buyNow?.[mentoring.id] ? (
                            <span className="truncate">
                              <span className="hidden sm:inline">Memproses...</span>
                              <span className="sm:hidden">Proses...</span>
                            </span>
                          ) : (
                            <span className="truncate">
                              <span className="hidden sm:inline">Beli Sekarang</span>
                              <span className="sm:hidden">Beli</span>
                            </span>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Owned/Purchased Status - Better success colors */}
          {(mentoring.is_purchased || mentoring.is_owned) && (
            <div
              className="p-3 sm:p-4 rounded-lg border-t-2"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                borderTopColor: "#05D172", // SECONDARY.DEFAULT
              }}
            >
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon
                    icon="material-symbols:check-circle"
                    className="w-5 h-5"
                    style={{ color: "#05D172" }} // SECONDARY.DEFAULT
                  />
                  <Badge
                    variant="success"
                    className="px-3 py-1 rounded-full text-sm font-semibold border"
                    style={{
                      backgroundColor: "rgba(5, 209, 114, 0.1)", // SECONDARY with opacity
                      color: "#029350", // SECONDARY.700
                      borderColor: "rgba(5, 209, 114, 0.3)",
                    }}
                  >
                    {mentoring.ownership_type === "subscription"
                      ? "Akses Berlangganan"
                      : mentoring.ownership_type === "direct_purchase"
                        ? "Aktif"
                        : mentoring.is_owned
                          ? "Aktif"
                          : "Tersedia"}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Icon
                    icon="material-symbols:person-outline"
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: "#636363" }} // STEPPER_SUB
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: "#636363" }} // STEPPER_SUB
                  >
                    {mentoring.mentor}
                  </span>
                </div>
                <p
                  className="text-xs"
                  style={{ color: "#636363" }} // STEPPER_SUB
                >
                  Sesi mentoring personal 1-on-1 ({mentoring.duration_minutes || 60} menit)
                </p>
                {/* Schedule Session Button */}
                <div className="flex justify-center">
                  <Button
                    variant={Button.VARIANTS.PRIMARY}
                    size={Button.SIZES.MEDIUM}
                    className="px-4 sm:px-6 py-2 rounded-full font-semibold text-sm text-white transition-all duration-200 hover:shadow-md"
                    style={{
                      backgroundColor: "#926fe5", // PRIMARY.DEFAULT
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#8553d8" // PRIMARY.600
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#926fe5"
                    }}
                    onClick={handleScheduleMentoring}
                  >
                    <Icon icon="material-symbols:schedule" className="w-4 h-4 mr-2" />
                    <span className="truncate">Jadwalkan Sesi</span>
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Contact/Not Purchasable Section */}
          {!mentoring.can_purchase && !mentoring.is_purchased && !mentoring.is_owned && (
            <div
              className="p-3 sm:p-4 rounded-lg border-t-2"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                borderTopColor: "#05D172", // SECONDARY.DEFAULT
              }}
            >
              <div className="flex flex-col gap-3 sm:gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Icon
                      icon="material-symbols:person-outline"
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: "#636363" }} // STEPPER_SUB
                    />
                    <span
                      className="text-sm font-medium"
                      style={{ color: "#636363" }} // STEPPER_SUB
                    >
                      {mentoring.mentor}
                    </span>
                  </div>
                  <p
                    className="text-xs"
                    style={{ color: "#636363" }} // STEPPER_SUB
                  >
                    Sesi mentoring personal 1-on-1 ({mentoring.duration_minutes || 60} menit)
                  </p>
                </div>
                <div className="flex justify-center">
                  <Button
                    variant={Button.VARIANTS.SECONDARY}
                    size={Button.SIZES.MEDIUM}
                    className="px-4 sm:px-6 py-2 rounded-full font-semibold text-sm text-white transition-all duration-200 hover:shadow-md"
                    style={{
                      backgroundColor: "#05D172", // SECONDARY.DEFAULT
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#00BC63" // SECONDARY.600
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#05D172"
                    }}
                    onClick={handleContactMentor}
                  >
                    <Icon icon="material-symbols:chat" className="w-4 h-4 mr-2" />
                    <span className="truncate">Hubungi Mentor</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MentoringCard
